import React, { ComponentType, useCallback, useMemo } from "react";

import { Dayjs } from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip as AntTooltip } from "antd";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
// Type-only import — pulls in the module augmentation that adds the
// `datalabels` key to `PluginOptionsByType`, so we can type-check the
// defensive `datalabels: { display: false }` block below without pulling
// the actual plugin into the runtime bundle.
import type {} from "chartjs-plugin-datalabels";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import RebetLoaderJs from "../../../shared/RebetLoader/RebetLoader";
import { formatDateNoTimezone } from "../../../../utils/dateService";
import {
  formatCount,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import type { UserAnalyticsDailyResult } from "../../../../queries/user-analytics/userAnalyticsDailyTypes";
import type { UserAnalyticsMonthlyResult } from "../../../../queries/user-analytics/userAnalyticsMonthlyTypes";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import type { AcquisitionSource } from "../../../../queries/acquisition/acquisitionSourceFilter";
import AcquisitionSourceSelect from "../AcquisitionSourceSelect";

import styles from "./DailyUsers.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;
const RebetLoader = RebetLoaderJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

// Backend payload uses snake_case (`signups`, `ftds`, `ftd_amount`). Each
// series binds a UI label / color to the corresponding key and tells the
// tooltip formatter whether to render as a count or as currency.
//
// `tooltipLabel` is the short noun that gets appended after the value in
// the per-day tooltip (e.g. "8 Users", "1 FTD") — distinct from `label`
// which feeds the chart legend ("First Time Deposits" reads better in the
// legend than the abbreviation).
const SERIES = [
  {
    key: "signups",
    label: "Users",
    tooltipLabel: "Users",
    color: "#22D88F",
    isCurrency: false,
  },
  {
    key: "ftds",
    label: "First Time Deposits",
    tooltipLabel: "FTD",
    color: "#A855F7",
    isCurrency: false,
  },
] as const;

/**
 * Presentational chart subsection of the Acquisition page.
 *
 * State (date range) is owned by the parent so the page-level CSV export
 * can read the active filter for this section without each child having
 * to bubble it back up. The hook call also lives in the parent — this
 * component just renders what it's handed.
 */
export interface DailyUsersProps {
  dates: DateRange;
  onDatesChange: (next: DateRange) => void;
  data: UserAnalyticsDailyResult | UserAnalyticsMonthlyResult | undefined;
  isFetching: boolean;
  isError: boolean;
  sources: AcquisitionSource[];
  onSourcesChange: (next: AcquisitionSource[]) => void;
  /** Picker preset highlight on first render (e.g. "Last 30 days"). */
  startRange?: string;
  presetOptions?: Array<{ label: string; value: DateRange }>;
  axisDateFormat?: string;
  tooltipDateFormat?: string;
  exportFilename?: string;
  sourceTestId?: string;
  testId?: string;
  ariaLabel?: string;
  errorMessage?: string;
  title?: string;
  titleTooltip?: string;
  includeFtdAmount?: boolean;
  picker?: "date" | "month";
}

const DailyUsers: React.FC<DailyUsersProps> = ({
  dates,
  onDatesChange,
  data,
  isFetching,
  isError,
  sources,
  onSourcesChange,
  startRange,
  presetOptions,
  axisDateFormat = "MMM D",
  tooltipDateFormat = "MMM D, YYYY",
  exportFilename = "acquisition-daily-users.xlsx",
  sourceTestId = "acquisition-daily-source-select",
  testId = "daily-users",
  ariaLabel = "Users daily breakdown",
  errorMessage = AppStrings.ACQUISITION_DAILY_LOAD_ERROR,
  title = AppStrings.USERS,
  titleTooltip,
  includeFtdAmount = true,
  picker,
}) => {
  const points = useMemo(() => data?.points ?? [], [data]);

  const currencySymbol = useCurrencySymbol() ?? "$";

  // Legacy DatePicker contract — the picker still calls back through
  // `getData`, but the real fetch is driven by `dates` -> hook -> React Query.
  const getData = useCallback((_queryString: string | null) => undefined, []);

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => formatDateNoTimezone(p.date, axisDateFormat)),
      datasets: SERIES.map((s) => ({
        label: s.label,
        data: points.map((p) => p[s.key]),
        backgroundColor: s.color,
        borderRadius: 3,
        // Bar-grouping geometry per spec:
        //   - barPercentage: 1.0      → bars within a single day touch
        //   - categoryPercentage: 0.7 → leaves a visible gap between days
        // Together this yields the "tight pair per day, breathing room
        // between days" look that matches the Users tab elsewhere in PAM.
        barPercentage: 1.0,
        categoryPercentage: 0.7,
      })),
    }),
    [axisDateFormat, points],
  );

  const chartOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#9aa3b1", font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(255,255,255,0.06)" },
          ticks: {
            color: "#9aa3b1",
            font: { size: 11 },
            // Compact 1k/2k/… for thousands+, raw integer below 1000.
            // Avoids displaying e.g. "0k" for values like 250 during rollout.
            callback: (tickValue) => {
              const n = Number(tickValue);
              if (!Number.isFinite(n)) return String(tickValue);
              return n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
            },
          },
        },
      },
      plugins: {
        legend: { display: false },
        title: { display: false },
        // Defensive: if the datalabels plugin gets globally registered
        // elsewhere, this stops it from drawing numbers on top of our bars.
        datalabels: { display: false },
        tooltip: {
          enabled: true,
          mode: "index",
          intersect: false,
          backgroundColor: "#252729",
          padding: 12,
          displayColors: true,
          titleFont: { family: "Poppins-regular", size: 13, weight: "bold" },
          bodyFont: { family: "Poppins-regular", size: 13 },
          callbacks: {
            title: (items) => {
              if (!items?.length) return "";
              const iso = points[items[0].dataIndex]?.date;
              return iso ? formatDateNoTimezone(iso, tooltipDateFormat) : "";
            },
            label: (item) => {
              const series = SERIES[item.datasetIndex];
              const value = Number(item.raw);
              const formatted = series?.isCurrency
                ? formatCurrency(value, currencySymbol)
                : formatCount(value);
              return series?.tooltipLabel
                ? `${formatted} ${series.tooltipLabel}`
                : formatted;
            },
          },
        },
      },
    }),
    [points, currencySymbol, tooltipDateFormat],
  );

  // The daily endpoint includes FTD amount; the monthly endpoint does not
  // request it, so monthly exports omit the column instead of implying zero.
  const exportRows = useMemo(
    () =>
      points.map((p) => {
        const row = {
          date: p.date,
          users: p.signups,
          first_time_deposits: p.ftds,
        };
        return includeFtdAmount && "ftd_amount" in p
          ? { ...row, ftd_amount: p.ftd_amount }
          : row;
      }),
    [includeFtdAmount, points],
  );

  return (
    <section
      className={styles.card}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <div className={styles.titleRow}>
            <div className={styles.titleWithInfo}>
              <h2 className={styles.title}>{title}</h2>
              {titleTooltip ? (
                <AntTooltip title={titleTooltip} placement="top">
                  <span
                    className={styles.infoIcon}
                    role="img"
                    aria-label={titleTooltip}
                  >
                    <InfoCircleOutlined />
                  </span>
                </AntTooltip>
              ) : null}
            </div>
            <AcquisitionSourceSelect
              value={sources}
              onChange={onSourcesChange}
              loading={isFetching}
              className={styles.sourceSelect}
              testId={sourceTestId}
            />
          </div>
          <ul className={styles.legend}>
            {SERIES.map((s) => (
              <li key={s.key} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={{ backgroundColor: s.color }}
                  aria-hidden="true"
                />
                {s.label}
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.controls}>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={onDatesChange}
            showPresets={true}
            startRange={startRange}
            presetOptions={presetOptions}
            picker={picker}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename={exportFilename}
          />
        </div>
      </header>

      {isError ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="daily-users-error"
        >
          {errorMessage}
        </p>
      ) : null}

      <div className={styles.chartContainer}>
        {isFetching ? (
          <div className={styles.loaderOverlay}>
            <RebetLoader width={100} height={100} />
          </div>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </div>
    </section>
  );
};

export default DailyUsers;
